import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Truck, Package, Radio, Eye, EyeOff, BadgePercent, AlertTriangle } from 'lucide-react';
import SiteFooter from '@/components/layout/SiteFooter';
import { Checkbox } from '@/components/ui/checkbox';
import BrandingGraphic from '@/components/BrandingGraphic';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralFromUrl = (searchParams.get('ref') || '').trim().toUpperCase();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [legalScrolledToEnd, setLegalScrolledToEnd] = useState(false);
  const [legalChecked, setLegalChecked] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    role: '',
    company: '',
    phone: '',
    referral_code: referralFromUrl,
    terms_accepted: false,
    policy_accepted: false,
    terms_scrolled_to_end: false,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(loginData.email, loginData.password);
      toast.success(`Welcome back, ${user.name}!`);
      navigate(`/${user.role}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.role) {
      toast.error('Please select a role');
      return;
    }
    if (!legalScrolledToEnd || !legalChecked) {
      toast.error('Please scroll and accept the legal agreement to continue');
      return;
    }
    setIsLoading(true);
    try {
      const user = await register({
        ...registerData,
        terms_accepted: true,
        policy_accepted: true,
        terms_scrolled_to_end: true,
      });
      toast.success(`Welcome to The Eagle, ${user.name}!`);
      navigate(`/${user.role}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const roleIcons = {
    distributor: <Package className="w-5 h-5" />,
    driver: <Truck className="w-5 h-5" />,
    dispatcher: <Radio className="w-5 h-5" />,
    sales_rep: <BadgePercent className="w-5 h-5" />,
    emergency: <AlertTriangle className="w-5 h-5" />,
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 grid lg:grid-cols-2">
      {/* Left - Branding */}
      <div className="hidden lg:flex flex-col justify-between bg-card p-12 relative overflow-hidden">
        <BrandingGraphic className="absolute inset-0" />
      </div>

      {/* Right - Auth Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="hidden lg:flex items-center justify-end gap-4 text-sm">
            <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">About</Link>
            <Link to="/investors" className="text-muted-foreground hover:text-foreground transition-colors">Investors</Link>
            <Link to="/careers" className="text-muted-foreground hover:text-foreground transition-colors">Careers</Link>
            <Link to="/emergency-services" className="text-muted-foreground hover:text-foreground transition-colors">Emergency Services</Link>
            <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
          </div>

          <BrandingGraphic compact className="lg:hidden mb-6" />

          <Link
            to="/emergency-services"
            className="block rounded-2xl border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-sm text-orange-50 transition-colors hover:bg-orange-500/15"
          >
            <p className="font-semibold tracking-wide">Emergency Services</p>
            <p className="mt-1 text-orange-100/80">View hospitals, police, fire, EMS, shelters, urgent care, and pharmacy routing support before signing in.</p>
          </Link>

          <Card className="border-border/50 shadow-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-heading tracking-tight">Welcome</CardTitle>
              <CardDescription>Sign in or create an account to continue</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@company.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90 shadow-glow font-bold uppercase tracking-wide"
                      disabled={isLoading}
                      data-testid="login-submit-btn"
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="John Doe"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@company.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        data-testid="register-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-role">Role</Label>
                      <Select
                        value={registerData.role}
                        onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                      >
                        <SelectTrigger data-testid="register-role-select">
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="distributor">
                            <div className="flex items-center gap-2">
                              {roleIcons.distributor}
                              <span>Distributor</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="driver">
                            <div className="flex items-center gap-2">
                              {roleIcons.driver}
                              <span>Truck Driver</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="dispatcher">
                            <div className="flex items-center gap-2">
                              {roleIcons.dispatcher}
                              <span>Dispatcher</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="sales_rep">
                            <div className="flex items-center gap-2">
                              {roleIcons.sales_rep}
                              <span>Sales Rep</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="emergency">
                            <div className="flex items-center gap-2">
                              {roleIcons.emergency}
                              <span>Emergency Responder</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-company">Company</Label>
                        <Input
                          id="register-company"
                          type="text"
                          placeholder="Optional"
                          value={registerData.company}
                          onChange={(e) => setRegisterData({ ...registerData, company: e.target.value })}
                          data-testid="register-company-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-phone">Phone</Label>
                        <Input
                          id="register-phone"
                          type="tel"
                          placeholder="Optional"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                          data-testid="register-phone-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Legal Agreement (Required)</Label>
                      <div
                        className="h-36 overflow-y-auto rounded-md border border-border p-3 text-xs text-muted-foreground bg-muted/30"
                        onScroll={(e) => {
                          const el = e.currentTarget;
                          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
                            setLegalScrolledToEnd(true);
                          }
                        }}
                        data-testid="legal-scroll-box"
                      >
                        <p className="mb-2 font-semibold text-foreground">Terms and Privacy Agreement</p>
                        <p>You agree to use The Eagle only for lawful logistics operations, provide accurate identity and shipment details, and comply with transport and safety rules.</p>
                        <p className="mt-2">You consent to processing of account, shipment, payment, chat, and location telemetry data for operational use, security, compliance, and audit records.</p>
                        <p className="mt-2">You are responsible for credential security and lawful document handling, including BOL and KYC documents where required.</p>
                        <p className="mt-2">The platform does not guarantee revenue, uninterrupted service, or delivery outcomes. Final operational decisions remain with users.</p>
                        <p className="mt-2">By continuing, you acknowledge Terms of Service and Privacy Policy as of March 8, 2026.</p>
                        <p className="mt-2 font-medium text-foreground">Scroll to the end before accepting.</p>
                      </div>
                      <div className="flex items-start gap-2 rounded-md border border-border p-3">
                        <Checkbox
                          id="legal-accept-checkbox"
                          checked={legalChecked}
                          onCheckedChange={(checked) => setLegalChecked(Boolean(checked))}
                          disabled={!legalScrolledToEnd}
                          data-testid="legal-accept-checkbox"
                        />
                        <Label htmlFor="legal-accept-checkbox" className="text-sm leading-relaxed cursor-pointer">
                          I have reviewed the agreement and accept the Terms and Privacy Policy.
                        </Label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-referral">Referral Code (Optional)</Label>
                      <Input
                        id="register-referral"
                        type="text"
                        placeholder="EGL-XXXX-XXXXXX"
                        value={registerData.referral_code}
                        onChange={(e) => setRegisterData({ ...registerData, referral_code: e.target.value.toUpperCase() })}
                        data-testid="register-referral-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90 shadow-glow font-bold uppercase tracking-wide"
                      disabled={isLoading || !legalScrolledToEnd || !legalChecked}
                      data-testid="register-submit-btn"
                    >
                      {isLoading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
      <SiteFooter compact />
    </div>
  );
};

export default LoginPage;
